import Layout from "../../components/Layout";
import { Helmet } from "react-helmet-async";
import {
  FiUsers,
  FiCalendar,
  FiTruck,
  FiClipboard,
  FiDollarSign,
  FiSettings,
  FiCheckCircle,
  FiBarChart2,
} from "react-icons/fi";

const IndustryHealthcare = () => {
  const trackCTA = () => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "click", {
        event_category: "CTA",
        event_label: "Sign Up - Healthcare Industry Page",
      });
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Healthcare Industry | RedERP</title>
        <meta
          name="description"
          content="RedERP empowers healthcare institutions to manage patient care, staff, procurement, and financials from one unified system."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-10">
          <div className="flex-1 text-left">
            <h1 className="text-4xl font-bold mb-4">ERP for Healthcare Providers</h1>
            <p className="mb-6 text-sm sm:text-base">
              Run clinics, medical centers, and hospitals more efficiently with streamlined procurement, HR, scheduling, and compliance tools.
            </p>
            <a
              href="https://app.rederp.pro/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackCTA}
              className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
            >
              Get Started Now
            </a>
          </div>
          <div className="flex-1 hidden md:flex justify-end">
            <img
              src="/assets/images/healthcare-industry.png"
              alt="Healthcare ERP"
              className="w-[400px] object-contain"
            />
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold text-primary mb-3">
            Modules Tailored for Healthcare Operations
          </h2>
          <p className="text-gray-600">
            RedERP is designed to simplify day-to-day operations for healthcare institutions of all sizes.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-left max-w-6xl mx-auto">
          {[
            {
              icon: <FiUsers size={24} />,
              title: "HR & Shift Scheduling",
              desc: "Manage shifts, time logs, payroll, and certifications for clinical and support staff.",
            },
            {
              icon: <FiCalendar size={24} />,
              title: "Appointment Scheduling",
              desc: "Track doctor availability, book consultations, and manage follow-up reminders.",
            },
            {
              icon: <FiTruck size={24} />,
              title: "Medical Procurement",
              desc: "Manage tenders and vendors for medical supplies, pharmaceuticals, and equipment.",
            },
            {
              icon: <FiDollarSign size={24} />,
              title: "Finance & Billing",
              desc: "Track expenses, generate insurance claims, and manage patient billing and co-payments.",
            },
            {
              icon: <FiClipboard size={24} />,
              title: "Compliance & Documentation",
              desc: "Ensure healthcare compliance, digital forms, and audit trails across operations.",
            },
            {
              icon: <FiBarChart2 size={24} />,
              title: "Analytics & KPIs",
              desc: "View dashboards on patient flow, utilization rates, treatment costs, and more.",
            },
            {
              icon: <FiCheckCircle size={24} />,
              title: "Inventory & Supplies",
              desc: "Track medicine batches, expiry dates, and surgical supply usage in real-time.",
            },
            {
              icon: <FiSettings size={24} />,
              title: "Custom Workflows",
              desc: "Adapt RedERP to unique clinical flows—labs, pharmacy, imaging, and admin.",
            },
          ].map((mod, i) => (
            <div
              key={i}
              className="p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3 text-primary mb-3">
                {mod.icon}
                <h3 className="font-bold">{mod.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{mod.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-10 py-16">
          <div className="flex-1 text-left">
            <h3 className="text-3xl font-bold mb-4">Transform Your Healthcare Operations</h3>
            <p className="mb-6 text-sm sm:text-base">
              From private clinics to healthcare chains, RedERP helps streamline operations and improve care.
            </p>
            <a
              href="https://app.rederp.pro/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackCTA}
              className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
            >
              Try RedERP for Healthcare
            </a>
          </div>
          <div className="flex-1 hidden md:flex justify-end">
            <img
              src="/assets/images/cta2.png"
              alt="Healthcare CTA"
              className="w-[360px] object-contain"
            />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default IndustryHealthcare;
